import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import {
  clampTimelineZoomLevel,
  getAdjacentTimelineZoomLevel,
} from '../lib/zoom';

export default function useTimelineViewport({
  zoom,
  setZoom,
  timeToPixels,
  liveFollowActive,
  setLiveFollowActive,
  isToday,
}) {
  const scrollRef = useRef(null);
  const suppressNextScrollUnfollowRef = useRef(false);
  const suppressScrollUnfollowTimeoutRef = useRef(null);
  const pendingZoomAnchorRef = useRef(null);
  const scrollAnimationFrameRef = useRef(null);

  const suppressProgrammaticScrollUnfollow = useCallback((durationMs = 300) => {
    suppressNextScrollUnfollowRef.current = true;
    if (suppressScrollUnfollowTimeoutRef.current) {
      window.clearTimeout(suppressScrollUnfollowTimeoutRef.current);
    }
    suppressScrollUnfollowTimeoutRef.current = window.setTimeout(() => {
      suppressNextScrollUnfollowRef.current = false;
      suppressScrollUnfollowTimeoutRef.current = null;
    }, durationMs);
  }, []);

  const cancelScrollAnimation = useCallback(() => {
    if (scrollAnimationFrameRef.current) {
      window.cancelAnimationFrame(scrollAnimationFrameRef.current);
      scrollAnimationFrameRef.current = null;
    }
  }, []);

  const animateHorizontalScrollTo = useCallback((targetLeft, options = {}) => {
    const el = scrollRef.current;
    if (!el) return;

    cancelScrollAnimation();
    const maxLeft = Math.max(0, el.scrollWidth - el.clientWidth);
    const nextLeft = Math.max(0, Math.min(Number(targetLeft) || 0, maxLeft));
    const startLeft = el.scrollLeft;
    const delta = nextLeft - startLeft;
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const durationMs = Number(options.durationMs ?? 850);

    if (prefersReducedMotion || !Number.isFinite(durationMs) || durationMs <= 0 || Math.abs(delta) < 1) {
      suppressProgrammaticScrollUnfollow(220);
      el.scrollLeft = nextLeft;
      return;
    }

    const easing = (progress) => (
      progress < 0.5
        ? 4 * progress * progress * progress
        : 1 - ((-2 * progress + 2) ** 3) / 2
    );
    suppressProgrammaticScrollUnfollow(durationMs + 180);
    const startedAt = window.performance.now();

    function step(now) {
      const progress = Math.min(1, (now - startedAt) / durationMs);
      el.scrollLeft = startLeft + (delta * easing(progress));
      if (progress < 1) {
        scrollAnimationFrameRef.current = window.requestAnimationFrame(step);
        return;
      }
      el.scrollLeft = nextLeft;
      scrollAnimationFrameRef.current = null;
    }

    scrollAnimationFrameRef.current = window.requestAnimationFrame(step);
  }, [cancelScrollAnimation, suppressProgrammaticScrollUnfollow]);

  const scrollToNow = useCallback((center = true, options = {}) => {
    const el = scrollRef.current;
    if (!el) return;

    const now = new Date();
    const minutes = now.getHours() * 60 + now.getMinutes();
    const x = timeToPixels(minutes, zoom);

    const targetLeft = center
      ? Math.max(x - el.clientWidth / 2, 0)
      : Math.max(x, 0);
    animateHorizontalScrollTo(targetLeft, options);
  }, [animateHorizontalScrollTo, timeToPixels, zoom]);

  const updateCurrentTimeLine = useCallback(() => {
    const line = document.getElementById('current-time-line');
    if (!line) return;

    if (!isToday) {
      line.style.display = 'none';
      return;
    }

    const now = new Date();
    const minutes = now.getHours() * 60 + now.getMinutes();
    line.style.left = `${timeToPixels(minutes, zoom)}px`;
    line.dataset.nowLabel = `Jetzt · ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    line.style.display = 'block';
  }, [isToday, timeToPixels, zoom]);

  const zoomWithAnchor = useCallback((nextZoom) => {
    const el = scrollRef.current;
    if (!el) return;

    const nz = clampTimelineZoomLevel(nextZoom);
    if (nz === zoom) return;

    const viewportCenter = el.clientWidth / 2;
    pendingZoomAnchorRef.current = {
      oldZoom: zoom,
      nextZoom: nz,
      anchorXInViewport: viewportCenter,
      contentXBefore: el.scrollLeft + viewportCenter,
    };

    setZoom(nz);
  }, [setZoom, zoom]);

  const zoomWithPointerAnchor = useCallback((nextZoom, clientX) => {
    const el = scrollRef.current;
    if (!el) return;

    const nz = clampTimelineZoomLevel(nextZoom);
    if (nz === zoom) return;

    const oldZoom = zoom;
    const rect = el.getBoundingClientRect();
    const pointerXInViewport = clientX - rect.left;

    const anchorXInViewport = Number.isFinite(pointerXInViewport)
      ? pointerXInViewport
      : (el.clientWidth / 2);
    pendingZoomAnchorRef.current = {
      oldZoom,
      nextZoom: nz,
      anchorXInViewport,
      contentXBefore: el.scrollLeft + anchorXInViewport,
    };

    setZoom(nz);
  }, [setZoom, zoom]);

  useLayoutEffect(() => {
    const pendingZoomAnchor = pendingZoomAnchorRef.current;
    const el = scrollRef.current;
    if (!pendingZoomAnchor || !el) {
      return;
    }

    if (pendingZoomAnchor.nextZoom !== zoom || pendingZoomAnchor.oldZoom === zoom) {
      return;
    }

    const scale = pendingZoomAnchor.nextZoom / pendingZoomAnchor.oldZoom;
    const contentXAfter = pendingZoomAnchor.contentXBefore * scale;

    suppressProgrammaticScrollUnfollow();
    el.scrollLeft = Math.max(contentXAfter - pendingZoomAnchor.anchorXInViewport, 0);
    pendingZoomAnchorRef.current = null;
  }, [suppressProgrammaticScrollUnfollow, zoom]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const onScroll = () => {
      if (suppressNextScrollUnfollowRef.current) return;
      if (liveFollowActive) setLiveFollowActive(false);
    };

    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, [liveFollowActive, setLiveFollowActive]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const onWheel = (e) => {
      if (!e.ctrlKey) return;
      e.preventDefault();

      const next = getAdjacentTimelineZoomLevel(zoom, e.deltaY < 0 ? 1 : -1);

      zoomWithPointerAnchor(next, e.clientX);
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [zoom, zoomWithPointerAnchor]);

  useEffect(() => {
    const onResize = () => updateCurrentTimeLine();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [updateCurrentTimeLine]);

  useEffect(() => () => {
    cancelScrollAnimation();
    if (suppressScrollUnfollowTimeoutRef.current) {
      window.clearTimeout(suppressScrollUnfollowTimeoutRef.current);
    }
  }, [cancelScrollAnimation]);

  useLayoutEffect(() => {
    updateCurrentTimeLine();
  }, [updateCurrentTimeLine]);

  return {
    scrollRef,
    scrollToNow,
    updateCurrentTimeLine,
    zoomWithAnchor,
    zoomWithPointerAnchor,
  };
}
